import { computed, Component, inject, input, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatSelectChange } from '@angular/material/select';
import { Router } from '@angular/router';
import { RootState } from '@next/core-lib/app';
import { BrandingService } from '@next/core-lib/branding';
import { DialogService } from '@next/core-lib/dialog';
import { LocaleService } from '@next/core-lib/i18n';
import { BreakpointService, BreakpointServiceInjector } from '@next/core-lib/responsive';
import { Store } from '@ngrx/store';

import {
  EGG_MARKETING_STANDARDS,
  HOUSING_SYSTEMS,
} from '../../../../../assets/data/eggMarketingStandards';
import * as barnActions from '../../../../settings/store/actions/barn.actions';
import {
  DrinkSystem,
  FeedSystem,
  FLOCK_VARIANT,
  Flock,
  FlockWeekDataSet,
  Nest,
} from '../../../models/flock.model';
import {
  loadDeliveryLinesRequest,
  loadHatcheriesRequest,
  loadRearingOrganizationsRequest,
  loadWeekRegistrationsRequest,
  deleteFlockWeekRegistrationWithConfirmation,
  deleteFlockWithConfirmation,
  setSelectedFlockId,
} from '../../../store/actions/flock.actions';
import {
  getFlocks,
  getIsDetailedViewSelected,
  getSelectedFlockId,
  getSelectedFlockWeekData,
} from '../../../store/selectors/flock.selectors';
import { ArrivalDetailComponent } from '../../arrival-detail/arrival-detail.component';
import { BreederWeekRegistrationComponent } from '../../breeders/week-registration/breeder-week-registration.component';
import { DepartureDetailComponent } from '../../departure-detail/departure-detail.component';
import { WeekRegistrationComponent } from '../../week-registration/week-registration.component';
import { FlockOverviewVariantConfig } from '../flock-variant.config';

@Component({
  selector: 'app-shared-flock-overview',
  templateUrl: './shared-flock-overview.component.html',
  styleUrls: ['./shared-flock-overview.component.scss'],
  standalone: false,
})
export class SharedFlockOverviewComponent {
  readonly config = input.required<FlockOverviewVariantConfig>();

  private readonly store = inject<Store<RootState>>(Store);
  private readonly breakpoint = inject<BreakpointService>(BreakpointServiceInjector);
  private readonly dialog = inject(DialogService);
  private readonly locale = inject(LocaleService);
  private readonly brandingService = inject(BrandingService);
  private readonly router = inject(Router);

  readonly flocks = toSignal(this.store.select(getFlocks), { initialValue: [] as Flock[] });
  readonly selectedFlockIdFromStore = toSignal(this.store.select(getSelectedFlockId), {
    initialValue: null,
  });
  readonly selectedFlockWeekData = toSignal(this.store.select(getSelectedFlockWeekData), {
    initialValue: undefined as FlockWeekDataSet | undefined,
  });
  readonly isDetailedWeekRegistrationView = toSignal(this.store.select(getIsDetailedViewSelected), {
    initialValue: false,
  });
  readonly isMobile = toSignal(this.breakpoint.ltSm$, { initialValue: false });
  readonly isLtLgBreakpoint = toSignal(this.breakpoint.ltLg$, { initialValue: false });
  private readonly manualSelectedFlockId = signal<number | null>(null);
  private readonly selectedFlockIdFromState = computed(() => {
    const flocks = this.flocks();
    const selectedFlockIdFromStore = this.selectedFlockIdFromStore();

    if (!flocks.length || selectedFlockIdFromStore === null) {
      return -1;
    }

    return selectedFlockIdFromStore === -1
      ? flocks[flocks.length - 1].id
      : (flocks.find((flock) => flock.id === selectedFlockIdFromStore)?.id ??
          flocks[flocks.length - 1].id);
  });
  private readonly selectedFlockFromState = computed(() => {
    const selectedFlockId = this.selectedFlockId;

    if (selectedFlockId === -1) {
      return undefined;
    }

    const selectedFlock = this.flocks().find((flock) => flock.id === selectedFlockId);
    return selectedFlock ? { ...selectedFlock } : undefined;
  });
  weekRegistrationSortBy = 'desc';

  get selectedFlock(): Flock | undefined {
    return this.selectedFlockFromState();
  }

  get selectedFlockId(): number {
    return this.manualSelectedFlockId() ?? this.selectedFlockIdFromState();
  }

  set selectedFlockId(value: number) {
    this.manualSelectedFlockId.set(value);
  }

  onWeekRegistrationSortByChanged(sortBy: string) {
    this.weekRegistrationSortBy = sortBy;
  }

  hasWeekRegistrationActions(): boolean {
    const cfg = this.config();
    return cfg.showLayerActions || cfg.flockVariant === FLOCK_VARIANT.Breeder;
  }

  canAddWeek(): boolean {
    const selectedFlock = this.selectedFlock;
    const weekRegistrations = selectedFlock?.weekRegistrations ?? [];

    if (weekRegistrations.length === 0) {
      return true;
    }

    if (selectedFlock?.departure != null) {
      const daysDifference = Math.round(
        Math.abs(
          new Date(<string>selectedFlock.departure.departureDate).getTime() -
            new Date(<string>selectedFlock.arrival.chickBirthDate).getTime(),
        ) /
          (1000 * 3600 * 24),
      );
      const weeks = [...weekRegistrations].sort((w1, w2) => (w1.weekNr > w2.weekNr ? 1 : -1));
      const lastDayInLastWeek = weeks[weekRegistrations.length - 1].endDay;

      // check if at least a day fits between last day in last week and departuredate
      return Math.abs(lastDayInLastWeek - daysDifference) >= 1;
    }
    return true;
  }

  addOrEditWeek(weekNumber?: number) {
    const selectedFlock = this.selectedFlock;
    if (!selectedFlock) {
      return;
    }

    const isAgravis = this.brandingService.isTenant('Agravis');
    const cfg = this.config();

    if (cfg.flockVariant === FLOCK_VARIANT.Layer) {
      this.store.dispatch(loadDeliveryLinesRequest());
    }
    this.store.dispatch(loadWeekRegistrationsRequest());

    const dialogComponent =
      cfg.flockVariant === FLOCK_VARIANT.Breeder
        ? BreederWeekRegistrationComponent
        : WeekRegistrationComponent;

    // Use undefined as weekNumber when adding a new week
    this.dialog.fullscreenDialog(
      dialogComponent as never,
      {
        weekNr: weekNumber,
        flock: selectedFlock,
        weekRegistrationConfig: cfg.weekRegistration,
        confirmButton: true,
        confirmButtonText: this.locale.translate('save'),
        altButton: weekNumber === undefined,
        altButtonText:
          weekNumber === undefined ? this.locale.translate('save-and-next-week') : null,
      },
      {
        autoFocus: false,
        panelClass: isAgravis
          ? ['mat-dialog-fullscreen', 'agravis-week-registration-dialog']
          : 'mat-dialog-fullscreen',
      },
    );
  }

  deleteWeek(weekNumber: number) {
    const selectedFlock = this.selectedFlock;
    if (!selectedFlock) {
      return;
    }

    this.store.dispatch(
      deleteFlockWeekRegistrationWithConfirmation({
        flockId: selectedFlock.id,
        weekNumber,
      }),
    );
  }

  openFlockDetail(flock?: Flock) {
    this.store.dispatch(barnActions.loadBarnsRequest());
    this.store.dispatch(loadHatcheriesRequest());

    const cfg = this.config();

    if (cfg.loadRearingOrganizations) {
      this.store.dispatch(loadRearingOrganizationsRequest());
    }

    this.dialog.form(ArrivalDetailComponent, {
      flock: flock ?? ({} as Flock),
      arrivalConfig: cfg.arrivalDetail,
      setPendingChanges: false,
    });
  }

  deleteFlock(flock: Flock) {
    this.store.dispatch(deleteFlockWithConfirmation({ id: flock.id }));
  }

  downloadPdf(selection: string) {
    const selectedFlock = this.selectedFlock;
    if (!selectedFlock) {
      return;
    }

    this.router.navigate(['flock', selection, selectedFlock.id]);
  }

  getMarketingStandard(marketingStandardId: number): string {
    return this.locale.translate(
      `egg-marketing-standards.${
        EGG_MARKETING_STANDARDS.find((standard) => standard.id === marketingStandardId)?.name
      }`,
    );
  }

  getHousingSystem(housingSystemId: number): string {
    return this.locale.translate(
      `housing-systems.${HOUSING_SYSTEMS.find((hs) => hs.id === housingSystemId)?.name}`,
    );
  }

  changeFlockSelection(selection: MatSelectChange) {
    this.setFlock(selection.value as number, true);
  }

  updateDeparture() {
    const selectedFlock = this.selectedFlock;
    if (!selectedFlock) {
      return;
    }

    this.dialog.form(DepartureDetailComponent, {
      flock: selectedFlock,
    });
  }

  setFlock(flockId: number, dispatch = false): void {
    this.selectedFlockId = flockId;

    if (dispatch) {
      this.store.dispatch(setSelectedFlockId({ id: flockId, save: true, reloadWeekData: true }));
    }
  }

  getLookupLabel(
    translationGroup: 'feed-systems' | 'drink-systems' | 'nests',
    value?: FeedSystem | DrinkSystem | Nest | null,
  ): string | null {
    if (!value) {
      return null;
    }

    const lookupName = typeof value === 'string' ? value : value.name;

    return this.locale.translate(`${translationGroup}.${lookupName}`);
  }

  displayValue<T>(value?: T | null): T | string {
    return value ?? '-';
  }

  hasRoosters(amountOfRoosters?: number | null): boolean {
    return (amountOfRoosters ?? 0) > 0;
  }
}
